import * as pc from "playcanvas";
import { MATCH_FORMAT } from "@hupper/shared";
import type { GameClient } from "../net/GameClient";
import type { GrenadeEffects, GrenadeKind } from "./GrenadeEffects";
import { buildGrenadeModel } from "../player/grenadeViewModel";
import type {
  GrenadeStateBroadcastMsg,
  GrenadeDetonateBroadcastMsg,
  ZoneStateBroadcastMsg,
  GrenadeKindWire,
} from "@hupper/shared/protocol";

/**
 * PeerGrenadeRenderer — Sprint 7.
 *
 * Drives all grenade visuals from server broadcasts:
 *   - `grenade_state` (per-tick) → render a marble for each in-flight
 *     grenade at its authoritative position. Visible to every client.
 *   - `grenade_detonate` (one-shot) → trigger the matching effect via
 *     the shared GrenadeEffects bus (HE explosion / flashbang / smoke
 *     cloud / molotov fire).
 *   - `zone_state` (per-tick) → render persistent smoke clouds + fire
 *     patches with positions / radii / lifetimes from the server.
 *
 * Grenades / zones not present in the latest snapshot are pruned. New
 * ones get their entities lazily.
 */
/** Backstop delay before force-clearing leftover grenade visuals after a
 *  normal round end. Comfortably longer than the server's post-round window
 *  (`between_rounds`, ~6 s) so a still-burning molotov is never cut short —
 *  by the time this fires the next round has already wiped the world. */
const ROUND_END_CLEAR_MS = 8000;

export class PeerGrenadeRenderer {
  private readonly app: pc.AppBase;
  private readonly effects: GrenadeEffects;
  private readonly client: GameClient;
  private readonly unsubs: Array<() => void> = [];

  /** Live marbles keyed by server-assigned grenade id. */
  private marbles = new Map<number, MarbleEntry>();
  /** Live zone visuals keyed by server-assigned zone id. */
  private zones = new Map<number, ZoneEntry>();
  /** Ids present in the most recent snapshot — for prune diff. */
  private lastGrenadeIds = new Set<number>();
  private lastZoneIds = new Set<number>();
  /** Backstop timer that force-clears grenade visuals a while after a round
   *  ends, in case the server's final prune broadcast is dropped. The server
   *  keeps the fire / smoke / explosions playing out (and re-broadcasting
   *  zone / grenade state) through `between_rounds` / the match-end grace, so
   *  this only fires well after the next round would already have wiped the
   *  world — it never cuts a still-burning molotov short. */
  private clearTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(app: pc.AppBase, client: GameClient, effects: GrenadeEffects) {
    this.app = app;
    this.effects = effects;
    this.client = client;
    this.unsubs.push(this.client.onGrenadeState((msg) => this.onGrenadeState(msg)));
    this.unsubs.push(this.client.onGrenadeDetonate((msg) => this.onGrenadeDetonate(msg)));
    this.unsubs.push(this.client.onZoneState((msg) => this.onZoneState(msg)));
    // Let the aftermath play out when a round ends instead of wiping it
    // instantly: the server keeps simulating fire / smoke / explosions (and
    // applying fire damage so a player can still burn to death in a lingering
    // molotov) through `between_rounds`, then prunes with a final empty
    // broadcast when the next round starts. We only force-clear as a delayed
    // backstop in case that prune broadcast is dropped — a longer wait after a
    // normal round end (the post-round window is several seconds), a shorter
    // one matched to the grace window when the whole match is over.
    this.unsubs.push(
      this.client.onRoundEnd((msg) => {
        this.scheduleClear(
          msg.matchOver ? MATCH_FORMAT.matchEndGraceSeconds * 1000 : ROUND_END_CLEAR_MS,
        );
      }),
    );
  }

  /** Force-clear grenade visuals after `delayMs` — a backstop for the
   *  server's final prune broadcast (which normally clears them first). */
  private scheduleClear(delayMs: number): void {
    if (this.clearTimer !== null) clearTimeout(this.clearTimer);
    this.clearTimer = setTimeout(() => {
      this.clearTimer = null;
      this.clearAll();
    }, delayMs);
  }

  /** Destroy every marble + zone visual and the shared detonation effects. */
  private clearAll(): void {
    for (const m of this.marbles.values()) m.entity.destroy();
    for (const z of this.zones.values()) z.destroy();
    this.marbles.clear();
    this.zones.clear();
    this.lastGrenadeIds.clear();
    this.lastZoneIds.clear();
    this.effects.clearAll();
  }

  destroy(): void {
    if (this.clearTimer !== null) {
      clearTimeout(this.clearTimer);
      this.clearTimer = null;
    }
    for (const u of this.unsubs) u();
    for (const m of this.marbles.values()) m.entity.destroy();
    for (const z of this.zones.values()) z.destroy();
    this.marbles.clear();
    this.zones.clear();
  }

  private onGrenadeState(msg: GrenadeStateBroadcastMsg): void {
    this.lastGrenadeIds.clear();
    for (const g of msg.grenades) {
      this.lastGrenadeIds.add(g.id);
      let entry = this.marbles.get(g.id);
      if (!entry) {
        entry = this.createMarble(g.kind);
        this.marbles.set(g.id, entry);
      }
      // Position update — snap to server pos (no interpolation; client
      // tick is fast enough and grenades move quickly).
      entry.entity.setPosition(g.x, g.y, g.z);
      // Cheap tumble for visual flavor.
      entry.tumble += 0.4;
      entry.entity.setLocalEulerAngles(entry.tumble * 1.6, entry.tumble * 1.2, 0);
    }
    // Prune any marbles no longer in the snapshot (server detonated or
    // we missed a packet). The detonate broadcast will arrive separately.
    for (const [id, entry] of this.marbles) {
      if (!this.lastGrenadeIds.has(id)) {
        entry.entity.destroy();
        this.marbles.delete(id);
      }
    }
  }

  private onGrenadeDetonate(msg: GrenadeDetonateBroadcastMsg): void {
    // Destroy the marble if still present.
    const marble = this.marbles.get(msg.id);
    if (marble) {
      marble.entity.destroy();
      this.marbles.delete(msg.id);
    }
    // Fire the matching visual + audio effect.
    this.effects.spawn(msg.kind as GrenadeKind, new pc.Vec3(msg.x, msg.y, msg.z));
  }

  private onZoneState(msg: ZoneStateBroadcastMsg): void {
    this.lastZoneIds.clear();
    for (const z of msg.zones) {
      this.lastZoneIds.add(z.id);
      let entry = this.zones.get(z.id);
      if (!entry) {
        entry = this.createZone(z.kind, z.radius);
        entry.entity.setPosition(z.x, z.y, z.z);
        this.zones.set(z.id, entry);
      }
      // Fade in at start, fade out at end so the cloud doesn't pop.
      const fadeIn = Math.min(1, z.ageSec / 0.4);
      const fadeOut = Math.min(1, z.remainingSec / 0.6);
      const alpha = Math.min(fadeIn, fadeOut);
      entry.setAlpha(alpha);
      entry.tick(this.app);
    }
    for (const [id, entry] of this.zones) {
      if (!this.lastZoneIds.has(id)) {
        entry.destroy();
        this.zones.delete(id);
      }
    }
  }

  private createMarble(kind: GrenadeKindWire): MarbleEntry {
    // Render the in-flight grenade as the same procedural model used for
    // the first-person viewmodel (HE pineapple / smoke + flash cans /
    // molotov bottle) instead of a plain sphere. Wire kind maps straight
    // to the viewmodel's weaponId branches.
    const e = new pc.Entity(`peer-grenade-${kind}`);
    buildGrenadeModel(e, kind);
    this.app.root.addChild(e);
    return { entity: e, tumble: 0 };
  }

  private createZone(kind: "smoke" | "fire", radius: number): ZoneEntry {
    if (kind === "smoke") {
      // CS:GO-style volumetric smoke — soft, camera-facing billboard puffs
      // packed into the zone sphere. Rendering + per-frame billboarding live
      // in GrenadeEffects (it owns the camera + update loop); the server
      // drives world position (set by the caller) and the fade in / out via
      // setAlpha. Replaces the old flat grey emissive spheres.
      const cloud = this.effects.createSmokeZone(radius);
      return {
        entity: cloud.root,
        setAlpha: (alpha) => cloud.setAlpha(alpha),
        tick: () => {},
        destroy: () => cloud.destroy(),
      };
    }

    // Fire patch — CS:GO-style flame pool. Billboarded flames + capped lights
    // are built + animated per frame by GrenadeEffects (it owns the camera +
    // update loop); the server drives world position (set by the caller) and
    // the fade in / out via setAlpha. Replaces the old additive sphere blob.
    const fire = this.effects.createFireZone(radius);
    return {
      entity: fire.root,
      setAlpha: (alpha) => fire.setAlpha(alpha),
      tick: () => {},
      destroy: () => fire.destroy(),
    };
  }
}

interface MarbleEntry {
  entity: pc.Entity;
  tumble: number;
}

interface ZoneEntry {
  entity: pc.Entity;
  setAlpha: (alpha: number) => void;
  tick: (app: pc.AppBase) => void;
  destroy: () => void;
}
